import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore";
import captureException from "@/func/sentryException.js";
import RiskService from "@/utils/risk/RiskProvider";
import axios from "axios";
import { useRiskJsLoader } from "@/hooks/useRiskJs";
import FormAccordionsDivided from "@/components/forms/formAccordionsDivided";
import { isDiscountActive, isDiscountEnabledForSelection } from "@/utils/discounts";

const Form = () => {
  const { isLoading, setLoadingState, isMobile } = useTemplateStore();
  const {
    paypalHandler,
    warrantyState,
    selectedProduct,
    discountCode,
    variants,
    isDiscountApplied,
    isSubscribe,
    premiumShippingUpsellEnabled,
  } = useCartData();
  const productData = useProductData();
  const { paymentsConfig } = productData || [];

  const riskReady = useRiskJsLoader(paymentsConfig?.checkoutcomPublisherKey);

  const riskService = useMemo(() => {
    if (!riskReady) return null;
    return new RiskService(paymentsConfig?.checkoutcomPublisherKey);
  }, [riskReady, paymentsConfig?.checkoutcomPublisherKey]);

  const formLabels = window.post_template?.checkout_v4_form.checkout_v4_formLabels;
  const formSections = window.post_template?.checkout_v4_form.checkout_v4_formHeadings;
  const [backError, setBackError] = useState();
  const backErrorField = useRef(null);
  const isSubmitting = useRef(false);
  const checkoutToken = useRef(window.checkoutToken || crypto.randomUUID());
  const discountsEnabledForSelection = isDiscountEnabledForSelection(productData, isSubscribe);
  const discountActive = isDiscountActive(productData, isSubscribe, discountCode, isDiscountApplied);
  const appliedDiscountCode = discountActive ? discountCode : null;
  const premiumShippingSettings = window.post_template?.checkout_v4_premium_shipping || {};
  const premiumShippingId = Number(premiumShippingSettings.checkout_v4_premium_shipping_konnektive_id);
  const hasPremiumShippingId = Number.isFinite(premiumShippingId) && premiumShippingId > 0;

  const scrollIntoView = (element) => {
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  };

  const cleanCheck = (isMobile == false && window.cleanDesktop) || (isMobile == true && window.cleanMobile);

  const directCheck = window.msg?.clean_checkout_if_direct_customer && window.directCheck;
  const enableCleanVersion = directCheck || cleanCheck;

  useEffect(() => {
    if (backError) {
      scrollIntoView(backErrorField?.current);
    }
  }, [backError]);

  const managePremiumShippingCookie = () => {
    if (premiumShippingUpsellEnabled && hasPremiumShippingId) {
      document.cookie = `premium_shipping=${premiumShippingId}; path=/; max-age=3600; SameSite=Lax`;
      return;
    }

    document.cookie = "premium_shipping=; path=/; max-age=0; SameSite=Lax";
  };

  const handlePaypalSubmit = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    managePremiumShippingCookie();
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;
    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;
    const nextStep = isSubscribe
      ? productData?.subscription?.subscription_next_page
      : productData?.next_page_on_success;

    const paypalData = {
      products: {
        currencyCode: productData?.currency_code,
        paypalMerchant: productData?.paypal_merchant,
        warrantyTitle: warrantyTitle,
        campaignId: isSubscribe
          ? productData?.subscription?.konnektive_campaign_id?.trim()
          : productData?.konnektive_campaign_id?.trim(),
        product: selectedProduct,
        nextStep: nextStep,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        isWarrantySelected: productData?.add_warranty_for_product ?? false,
        warrantyState: warrantyState,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        postId: window.postId,
        variants: variantsEnabled && variants.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      nextStep: nextStep,
      upsell: productData?.paypal_upsell ? productData?.paypal_upsell_settings : null,
      thankYou: productData?.paypal_thank_you_page,
      return_to: window.location.href,
      affiliate: window.affiliate,
      checkoutToken: checkoutToken.current,
      discountsEnabled: discountsEnabledForSelection,
      appliedDiscountCode: appliedDiscountCode,
      isDiscountApplied: discountActive,
      premiumShippingUpsellEnabled: premiumShippingUpsellEnabled,
      subscription: {
        ...productData?.subscription,
        subscription_active: isSubscribe,
      },
    };

    try {
      setLoadingState(true);
      const response = await axios.post("/wp-json/checkout/v1/order/paypal", paypalData);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        const errorObj = new Error(`Checkout payment error: ${data.message}`);
        captureException(errorObj, {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: {
            response_error: data.message,
            payment_type: "paypal_order",
          },
          contexts: {
            request_data: paypalData,
          },
        });
      } else {
        window.location.href = data;
      }
    } catch (error) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: {
          response_error: error?.response?.data,
          payment_type: "paypal_order",
        },
        contexts: {
          request_data: paypalData,
        },
      });
    }
  };

  const handleCreditCardSubmit = async (formData) => {
    const placedOrderId = sessionStorage.getItem("cc_order_placed_" + checkoutToken.current);
    if (placedOrderId) {
      const nextStep = isSubscribe
        ? productData?.subscription?.subscription_next_page
        : productData?.next_page_on_success;
      window.location.href = nextStep + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    managePremiumShippingCookie();
    const navigator = window.navigator;
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;
    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;
    const nextStep = isSubscribe
      ? productData?.subscription?.subscription_next_page
      : productData?.next_page_on_success;
    setLoadingState(true);
    const riskSessionId = (riskService ? await riskService.getSessionId() : null) ?? null;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      data: formData,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: isSubscribe
          ? productData?.subscription?.konnektive_campaign_id?.trim()
          : productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: nextStep,
        product: selectedProduct,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: variantsEnabled && variants?.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_path: nextStep,
      return_to: window.location.href,
      browserData: browserData,
      paypalMerchant: productData?.paypal_merchant,
      checkoutToken: checkoutToken.current,
      discountsEnabled: discountsEnabledForSelection,
      appliedDiscountCode: appliedDiscountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: discountActive,
      riskSessionId: riskSessionId,
      premiumShippingUpsellEnabled: premiumShippingUpsellEnabled,
      subscription: {
        ...productData?.subscription,
        subscription_active: isSubscribe,
      },
    };

    try {
      const response = await axios.post("/wp-json/checkout/v1/order", requestData);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        const ErrorObj = new Error(`Checkout payment error: ${data.message}`);
        captureException(ErrorObj, {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: {
            response_error: data.message,
            payment_type: "credit_card",
          },
          contexts: {
            request_data: requestData,
          },
        });
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem("cc_order_placed_" + checkoutToken.current, data.message.orderId);
        window.location.href = nextStep + "?orderId=" + data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        const message = data.message;
        if (message && message.url) {
          window.location.href = message.url;
        } else if (message && message.script) {
          eval(message.script);
        }
      }
    } catch (error) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: {
          response_error: error?.message,
          payment_type: "credit_card",
        },
        contexts: {
          request_data: requestData,
        },
      });
    }
  };

  const handleKlarnaSubmit = async (formData) => {
    if (!formData) {
      return;
    }
    const placedOrderId = sessionStorage.getItem("cc_order_placed_" + checkoutToken.current);
    if (placedOrderId) {
      const productData = useProductData.getState();
      window.location.href = productData?.next_page_on_success + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    managePremiumShippingCookie();

    const productData = useProductData.getState();
    const removeWarrantyWithKlarna = productData?.allowed_payment_methods?.remove_warranty_with_klarna || false;

    const { warrantyState, selectedProduct, discountCode, variants, isDiscountApplied } = useCartData.getState();

    const navigator = window.navigator;
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;

    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;

    const nextStep = isSubscribe
      ? productData?.subscription?.subscription_next_page
      : productData?.next_page_on_success;

    const finalWarrantyState = removeWarrantyWithKlarna ? false : warrantyState;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      data: formData,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: isSubscribe
          ? productData?.subscription?.konnektive_campaign_id?.trim()
          : productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: nextStep,
        product: selectedProduct,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: finalWarrantyState,
        variants: variantsEnabled && variants?.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_path: nextStep,
      return_to: window.location.href,
      browserData: browserData,
      paypalMerchant: productData?.paypal_merchant,
      checkoutToken: checkoutToken.current,
      discountsEnabled: discountsEnabledForSelection,
      appliedDiscountCode: appliedDiscountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: discountActive,
      premiumShippingUpsellEnabled: premiumShippingUpsellEnabled,
      subscription: {
        ...productData?.subscription,
        subscription_active: isSubscribe,
      },
    };

    const klarnaCountryError =
      window.post_template?.checkout_v4_form?.checkout_v4_errors?.tr_checkout_v4_form_invalidCountry;

    try {
      setLoadingState(true);
      const response = await axios.post("/wp-json/checkout/v1/order/klarna", requestData, options);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem("cc_order_placed_" + checkoutToken.current, data.message.orderId);
        window.location.href = productData?.next_page_on_success + "?orderId=" + data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        const message = data.message;
        if (message && message.url) {
          window.location.href = message.url;
        } else if (message && message.script) {
          eval(message.script);
        }
      }
    } catch (error) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error.status === 422 ? klarnaCountryError : error.message);
    }
  };

  const handleApplePaySubmit = async (applePayData, options = {}) => {
    const placedOrderId = sessionStorage.getItem("cc_order_placed_" + checkoutToken.current);
    if (placedOrderId) {
      const productData = useProductData.getState();
      const nextStep = isSubscribe
        ? productData?.subscription?.subscription_next_page
        : productData?.next_page_on_success;
      window.location.href = nextStep + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    managePremiumShippingCookie();
    // We need updated version of productData
    const productData = useProductData.getState();
    const { warrantyState, selectedProduct, discountCode, variants, isDiscountApplied } = useCartData.getState();

    const navigator = window.navigator;
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;

    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;
    const nextStep = isSubscribe
      ? productData?.subscription?.subscription_next_page
      : productData?.next_page_on_success;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      apple_pay_token: applePayData.apple_pay_token,
      applePayProvider: applePayData.applePayProvider,
      data: applePayData.customer,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: isSubscribe
          ? productData?.subscription?.konnektive_campaign_id?.trim()
          : productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: nextStep,
        product: selectedProduct,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: variantsEnabled && variants?.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_path: nextStep,
      return_to: window.location.href,
      browserData: browserData,
      paypalMerchant: productData?.paypal_merchant,
      checkoutToken: checkoutToken.current,
      discountsEnabled: discountsEnabledForSelection,
      appliedDiscountCode: appliedDiscountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: discountActive,
      premiumShippingUpsellEnabled: premiumShippingUpsellEnabled,
      subscription: {
        ...productData?.subscription,
        subscription_active: isSubscribe,
      },
    };

    try {
      setLoadingState(true);
      const response = await axios.post("/wp-json/checkout/v1/order/applepay/pay", requestData, options);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        const ErrorObj = new Error(`Checkout payment error: ${data.message}`);
        captureException(ErrorObj, {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: {
            response_error: data.message,
            payment_type: "apple_pay",
          },
          contexts: {
            request_data: requestData,
          },
        });
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem("cc_order_placed_" + checkoutToken.current, data.message.orderId);
        window.location.href = nextStep + "?orderId=" + data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        const message = data.message;
        if (message && message.url) {
          window.location.href = message.url;
        } else if (message && message.script) {
          eval(message.script);
        }
      }
    } catch (error) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: {
          response_error: error?.message,
          payment_type: "apple_pay",
        },
        contexts: {
          request_data: requestData,
        },
      });
    }
  };

  const handleGooglePaySubmit = async (googlePayData, options = {}) => {
    const placedOrderId = sessionStorage.getItem("cc_order_placed_" + checkoutToken.current);
    if (placedOrderId) {
      const productData = useProductData.getState();
      const nextStep = isSubscribe
        ? productData?.subscription?.subscription_next_page
        : productData?.next_page_on_success;
      window.location.href = nextStep + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    managePremiumShippingCookie();
    // We need updated version of productData
    const productData = useProductData.getState();
    const { warrantyState, selectedProduct, discountCode, variants, isDiscountApplied } = useCartData.getState();

    const navigator = window.navigator;
    const variantsEnabled = productData?.enable_color_variants || productData?.enable_size_variants;

    const shippingProtectionLabel =
      window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_shippingProtectionLabel;
    const extendedWarrantyLabel = window.post_template?.checkout_v4_summary?.tr_checkout_v4_summary_warrantyLabel;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty" ? extendedWarrantyLabel : shippingProtectionLabel;
    const nextStep = isSubscribe
      ? productData?.subscription?.subscription_next_page
      : productData?.next_page_on_success;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      google_pay_token: googlePayData.google_pay_token,
      data: googlePayData.customer,

      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: isSubscribe
          ? productData?.subscription?.konnektive_campaign_id?.trim()
          : productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: nextStep,
        product: selectedProduct,
        warranty_price: productData?.add_warranty_for_product === false ? 0 : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation: productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: variantsEnabled && variants?.length > 0 ? variants?.slice(0, selectedProduct?.amount) : null,
      },
      upsell_path: nextStep,
      return_to: window.location.href,
      browserData: browserData,
      paypalMerchant: productData?.paypal_merchant,
      checkoutToken: checkoutToken.current,
      discountsEnabled: discountsEnabledForSelection,
      appliedDiscountCode: appliedDiscountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: discountActive,
      premiumShippingUpsellEnabled: premiumShippingUpsellEnabled,
      subscription: {
        ...productData?.subscription,
        subscription_active: isSubscribe,
      },
    };

    try {
      setLoadingState(true);
      const response = await axios.post("/wp-json/checkout/v1/order/googlepay/pay", requestData, options);
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        const ErrorObj = new Error(`Checkout payment error: ${data.message}`);
        captureException(ErrorObj, {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: {
            response_error: data.message,
            payment_type: "google_pay",
          },
          contexts: {
            request_data: requestData,
          },
        });
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem("cc_order_placed_" + checkoutToken.current, data.message.orderId);
        window.location.href = nextStep + "?orderId=" + data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        const message = data.message;
        if (message && message.url) {
          window.location.href = message.url;
        } else if (message && message.script) {
          eval(message.script);
        }
      }
    } catch (error) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: {
          response_error: error?.message,
          payment_type: "google_pay",
        },
        contexts: {
          request_data: requestData,
        },
      });
    }
  };

  return (
    <>
      <FormAccordionsDivided
        labels={formLabels}
        headings={formSections}
        formSubmited={isLoading}
        handleCreditCardSubmit={handleCreditCardSubmit}
        handlePaypalSubmit={handlePaypalSubmit}
        handleApplePaySubmit={handleApplePaySubmit}
        handleGooglePaySubmit={handleGooglePaySubmit}
        handleKlarnaSubmit={handleKlarnaSubmit}
        compliance={false}
        cleanVersion={!!enableCleanVersion}
        paypalHandler={paypalHandler}
      />
      {backError && (
        <div ref={backErrorField} className="error-back">
          {backError}
        </div>
      )}
    </>
  );
};

export { Form };
